import React, { useState, useEffect } from 'react';
import StaffNavbar from '../components/StaffNavbar';
import StaffFansStatsCard from '../components/StaffFansStatsCard';
import Icon from '../components/Icon';
import apiService from '../services/api';
import '../styles/StaffPages.css';

const StaffFans = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalLifetimePoints: 0,
    averageLifetimePoints: 0,
    loading: true
  });

  const [users, setUsers] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await apiService.getAllUsersStaff();
        const users = response.users || [];

        const totalUsers = users.length;
        const totalLifetimePoints = users.reduce((sum, user) => sum + (user.lifetime_points || 0), 0);
        const averageLifetimePoints = totalUsers > 0 ? Math.round(totalLifetimePoints / totalUsers) : 0;

        setStats({
          totalUsers,
          totalLifetimePoints,
          averageLifetimePoints,
          loading: false
        });

        setUsers(users);
      } catch (error) {
        console.error('Failed to fetch user stats:', error);
        setStats(prev => ({ ...prev, loading: false }));
      }
    };

    fetchStats();
  }, []);


  const formatDate = (dateString) => {
    if (!dateString || dateString === 'Never' || dateString === 'N/A') {
      return dateString;
    }
    try {
      const date = new Date(dateString);
      const month = date.getMonth() + 1; // getMonth() returns 0-11
      const day = date.getDate();
      const year = date.getFullYear();
      return `${month}/${day.toString().padStart(2, '0')}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedUsers = React.useMemo(() => {
    let filteredUsers = users;

    // Filter by search term (name or email)
    if (searchTerm) {
      filteredUsers = users.filter(user => {
        const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
        const email = user.email.toLowerCase();
        const search = searchTerm.toLowerCase();
        return fullName.includes(search) || email.includes(search);
      });
    }

    // Sort filtered results
    if (!sortConfig.key) {
      return filteredUsers;
    }

    return [...filteredUsers].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'name':
          aValue = `${a.first_name} ${a.last_name}`.toLowerCase();
          bValue = `${b.first_name} ${b.last_name}`.toLowerCase();
          break;
        case 'email':
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        case 'points':
          aValue = a.lifetime_points || 0;
          bValue = b.lifetime_points || 0;
          break;
        case 'lastActivity':
          aValue = new Date(a.last_login || 0);
          bValue = new Date(b.last_login || 0);
          break;
        case 'registrationDate':
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case 'type':
          aValue = a.is_staff ? 'Staff' : 'Fan';
          bValue = b.is_staff ? 'Staff' : 'Fan';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [users, sortConfig, searchTerm]);

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="fans" />
      <div className="admin-main">
        <div className="admin-content">
          <h1 className="staff-page-header">Fans Management</h1>
          <p className="staff-page-subheader">Manage and monitor your school's registered fans and their activity.</p>

          <div style={{
            display: 'flex',
            gap: '8px',
            marginTop: '24px',
            flexWrap: 'wrap'
          }}>
            <div style={{
              flex: '1 1 200px',
              minWidth: '200px',
              maxWidth: '33%'
            }}>
              <StaffFansStatsCard
                title="Total Fans"
                value={stats.loading ? "..." : stats.totalUsers}
                iconName="people"
              />
            </div>
            <div style={{
              flex: '1 1 200px',
              minWidth: '200px',
              maxWidth: '33%'
            }}>
              <StaffFansStatsCard
                title="Points Awarded"
                value={stats.loading ? "..." : stats.totalLifetimePoints.toLocaleString()}
                iconName="activity"
              />
            </div>
            <div style={{
              flex: '1 1 200px',
              minWidth: '200px',
              maxWidth: '33%'
            }}>
              <StaffFansStatsCard
                title="Average Points"
                value={stats.loading ? "..." : stats.averageLifetimePoints.toLocaleString()}
                iconName="points"
              />
            </div>
          </div>

          <h3 style={{
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: '700',
            lineHeight: 'normal',
            marginTop: '32px',
            marginBottom: '4px',
          }}>
            All Fans
          </h3>

          <p style={{
            color: 'var(--Black, #000)',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: '340',
            lineHeight: 'normal',
            opacity: '0.5',
            marginTop: '0',
            marginBottom: '16px'
          }}>
            A comprehensive list of all registered fans and their activity
          </p>

          <div style={{
            display: 'flex',
            width: '400px',
            height: '44px',
            padding: '10px 14px',
            alignItems: 'center',
            gap: '8px',
            borderRadius: '12px',
            border: '0.5px solid rgba(0, 0, 0, 0.50)',
            marginBottom: '24px'
          }}>
            <div style={{
              opacity: '0.5',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Icon name="search" size={18} />
            </div>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                border: 'none',
                outline: 'none',
                flex: 1,
                fontSize: '14px',
                backgroundColor: 'transparent',
                fontFamily: '"Baikal Book"',
                display: 'flex',
                alignItems: 'center'
              }}
            />
          </div>

          <div style={{
            display: 'flex',
            padding: '18px',
            alignItems: 'center',
            alignSelf: 'stretch',
            borderRadius: '12px 12px 0 0',
            background: '#F8F8F8',
            backdropFilter: 'blur(22.5px)'
          }}>
            <div
              onClick={() => handleSort('name')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '20%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Fan Name
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('email')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '20%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Email
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('points')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '15%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Points
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('lastActivity')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '15%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Last Activity
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('registrationDate')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '20%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Registration Date
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('type')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '10%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Type
              <Icon name="up-down" size={12} />
            </div>
          </div>

          <div style={{ marginBottom: '8px' }}></div>

          {sortedUsers.map((user, index) => (
            <div key={user.id || index} style={{
              display: 'flex',
              width: '100%',
              maxWidth: '1270px',
              height: '59px',
              padding: '18px',
              alignItems: 'center',
              borderRadius: '12px',
              background: '#F8F8F8',
              marginBottom: '8px'
            }}>
              <div style={{
                fontFamily: '"Baikal Book"',
                fontSize: '14px',
                width: '20%',
                minWidth: '0',
                flexShrink: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: '59px',
                paddingRight: '12px'
              }}>
                {`${user.first_name} ${user.last_name}`}
              </div>
              <div style={{
                fontFamily: '"Baikal Book"',
                fontSize: '14px',
                width: '20%',
                minWidth: '0',
                flexShrink: 0,
                opacity: '0.5',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: '59px',
                paddingRight: '12px'
              }}>
                {user.email}
              </div>
              <div style={{
                fontFamily: '"Baikal Book"',
                fontSize: '14px',
                width: '15%',
                minWidth: '0',
                flexShrink: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: '59px',
                paddingRight: '12px'
              }}>
                {(user.lifetime_points || 0).toLocaleString()}
              </div>
              <div style={{
                fontFamily: '"Baikal Book"',
                fontSize: '14px',
                width: '15%',
                minWidth: '0',
                flexShrink: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: '59px',
                paddingRight: '12px'
              }}>
                {formatDate(user.last_login) || 'Never'}
              </div>
              <div style={{
                fontFamily: '"Baikal Book"',
                fontSize: '14px',
                width: '20%',
                minWidth: '0',
                flexShrink: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: '59px',
                paddingRight: '12px'
              }}>
                {formatDate(user.created_at) || 'N/A'}
              </div>
              <div style={{
                width: '10%',
                display: 'flex',
                alignItems: 'center'
              }}>
                {user.is_staff ? (
                  <span style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    display: 'flex',
                    width: 'max-content',
                    height: 'max-content',
                    padding: '4px 8px',
                    alignItems: 'center',
                    gap: '10px',
                    borderRadius: '100px',
                    background: 'rgba(0, 0, 0, 0.06)'
                  }}>
                    Staff
                  </span>
                ) : (
                  <span style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    display: 'flex',
                    width: 'max-content',
                    height: 'max-content',
                    padding: '4px 8px',
                    alignItems: 'center',
                    gap: '10px',
                    borderRadius: '100px',
                    background: '#CEE8EF'
                  }}>
                    Fan
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StaffFans;